import { Text } from '@genial/components';
import { useBreakpointValue } from '@genial/utils';

import { TableContentItemText } from '../../../../components/TableContent';
import { IOProductItem } from '../../../../store/customerDetail/SecuritiesTransfer/types';

import { DynamicIOProductTableDesktop } from './desktop';
import { DynamicIOProductTableMobile } from './mobile';
import { DynamicIOProductTableColumn, DynamicIOProductTableProps } from './types';
import type { DynamicIOProductRenderColumnValue, DynamicIOProductRenderTitleValue } from './types';

export type {
  DynamicIOProductTableValue,
  DynamicIOProductTableColumn,
  DynamicIOProductTableProps,
  DynamicIOProductTableLayoutProps,
  DynamicIOProductRenderColumnValue,
  DynamicIOProductRenderTitleValue,
} from './types';

const getTextValue = (value: unknown) => {
  if (value == null) {
    return '';
  }

  return String(value);
};

const renderColumnValue: DynamicIOProductRenderColumnValue = <
  T extends IOProductItem,
  K extends keyof T
>(
  item: T,
  column: DynamicIOProductTableColumn<T, K>,
  isMobile: boolean
) => {
  const value = item[column.dataKey];

  if (column.render) {
    return column.render({
      item,
      value,
      isMobile,
    });
  }

  const textValue = getTextValue(value);

  if (isMobile) {
    return <Text>{textValue}</Text>;
  }

  return <TableContentItemText>{textValue}</TableContentItemText>;
};

const renderTitleValue: DynamicIOProductRenderTitleValue = <T extends IOProductItem>(
  item: T,
  titleDataKey: keyof T,
  renderTitle: DynamicIOProductTableProps<T>['renderTitle'],
  isMobile: boolean
) => {
  const value = item[titleDataKey];

  if (renderTitle) {
    return renderTitle({
      item,
      value,
      isMobile,
    });
  }

  const textValue = getTextValue(value);

  if (isMobile) {
    return <Text variant="semibold">{textValue}</Text>;
  }

  return <TableContentItemText>{textValue}</TableContentItemText>;
};

export const DynamicIOProductTable = <T extends IOProductItem>({
  title,
  titleDataKey,
  data,
  columns,
  keyExtractor,
  onPressItem,
  renderTitle,
}: DynamicIOProductTableProps<T>) => {
  const isMobile = useBreakpointValue({
    base: true,
    lg: false,
  });
  const detailColumns = columns.filter((column) => column.mobilePlacement !== 'footer');
  const footerColumns = columns.filter((column) => column.mobilePlacement === 'footer');

  if (!data.length) {
    return null;
  }

  const layoutProps = {
    title,
    titleDataKey,
    data,
    columns,
    keyExtractor,
    onPressItem,
    renderTitle,
    detailColumns,
    footerColumns,
    renderColumnValue,
    renderTitleValue,
  };

  return isMobile ? (
    <DynamicIOProductTableMobile {...layoutProps} />
  ) : (
    <DynamicIOProductTableDesktop {...layoutProps} />
  );
};
